/**
 * Can be used to trigger global event throughout the Vue project.
 * The class is available as $bus in your component or store
 * 
 * @autor Michel Heitbrink
 */
 class VueEventBus {

	constructor() {
		this.listeners = [];
	}

	/**
	 * Trigger a global event throughout the project
	 *  
	 * @param {String} name 
	 * @param {?Object} data 
	 */
	emit(name, data) {
		const candidates = this.listeners.filter( l => l.name == name );

		candidates.forEach( (candidate) => {
			candidate['callback'](data);
		});
	}

	/**
	 * Registrate a global event. Can be triggert with the emit function
	 * Returns a reference. Can be used to remove the event
	 * 
	 * @param {String} name 
	 * @param {Function} callback 
	 * @returns {String} reference
	 */
	on(name, callback) {

		if ( typeof callback == "function" ) {
			let reference = Math.random().toString(36).substring(2, 20);
			this.listeners.push({ callback, name, reference });
			return reference;
		}
		else {
			console.error('VueEventBus: callback isn\'t a function');
		}
	}

	/**
	 * Remove a global event based on name and reference.
	 * The reference can be the same function used at the on function
	 * Or can be the string reference returned by the `on` function 
	 * 
	 * @param {String} name 
	 * @param {String|Function} reference 
	 */
	off(name, reference) {
		if ( typeof reference == 'string' ) {
			this.listeners = this.listeners.filter( l => l.name != name || l.reference != reference );
		}
		else {
			this.listeners = this.listeners.filter( l => l.name != name || l.callback != reference );
		}
	}
}

export default {
	install: (app, options) => {
		options ??= {};
		
		let _bus = new VueEventBus(options);
        
        /**
		 * Adding bus Class to Vue Instance
		 */
		app.config.globalProperties.$bus = _bus;

		/**
		 * Adding bus Class to Store
		 */
		if ( app.config.globalProperties.$store ) {
			app.config.globalProperties.$store.$bus = _bus;
		}
	}
}

/*
---
name: Event Bus
category: Application
subcategory: Vue
---

The Event Bus plugin adds the possibility to use global events throughout the project. This plugin doens't replace the native component events:
https://vuejs.org/guide/components/events.html


```js
import { createApp, h } from 'vue';
import { VueEventBus } from '@nutshell/vue';

const vueApp = createApp({
	render: () => h(App)
});

//Registrate plugin
vueApp.use(VueEventBus);

```

```js
//usage Examples
<script>
export default {
    methods: {
        example() {
            alert('test');
        }
    },
	mounted() {
        //Add a function to the global event bus 
		let ref = this.$bus.on('example', this.example);

        //Remove listener based on function
		this.$bus.off('example', this.example);

        //Remove listener based on reference
		this.$bus.off('example', ref);

        //Trigger global event
        this.$bus.emit('example', {
            'test': 'coolminds'
        });
	}
}
</script>
```
*/
